export const arrivalText = (value) =>
  value === null || value === undefined || String(value).trim() === ''
    ? '-'
    : String(value).trim()

export const formatArrivalDate = (value, includeTime = false) => {
  const match = String(value || '').match(
    /^(\d{4})[-/](\d{2})[-/](\d{2})(?:[T ](\d{2}):(\d{2}):(\d{2}))?/
  )
  if (!match || (includeTime && !match[4])) return '-'
  const date = `${match[1]}/${match[2]}/${match[3]}`
  return includeTime ? `${date} ${match[4]}:${match[5]}:${match[6]}` : date
}

export const formatPopupDateTime = (value, includeSeconds = true) => {
  const match = String(value || '').match(
    /^(\d{4})[-/](\d{2})[-/](\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/
  )
  if (!match || match[1] === '1900') return '-'

  const [, year, month, day, hour, minute, second] = match
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)))
  if (
    date.getUTCFullYear() !== Number(year) ||
    date.getUTCMonth() !== Number(month) - 1 ||
    date.getUTCDate() !== Number(day)
  ) {
    return '-'
  }

  return {
    date: `${year.slice(-2)}/${month}/${day}`,
    time: hour && minute
      ? `${hour}:${minute}${includeSeconds && second ? `:${second}` : ''}`
      : '-'
  }
}

// Compare the calendar date shown on the arrival card, without converting
// timestamps through the browser's local timezone.
const getCalendarDate = (value) => {
  const match = String(value ?? '').match(/^(\d{4})[-/](\d{2})[-/](\d{2})(?:$|[T ])/)
  if (!match) return null
  const [, year, month, day] = match
  const date = new Date(`${year}-${month}-${day}T00:00:00Z`)
  const normalized = `${year}-${month}-${day}`
  return Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== normalized
    ? null
    : normalized
}

export const getShippingDate = (item) =>
  getCalendarDate(item?.arrival_info?.arrival_shipping_at)

export const filterArrivalItemsByShippingDate = (items, startDate, endDate) => {
  if (!startDate && !endDate) return items
  if (startDate && endDate && startDate > endDate) return []
  return items.filter((item) => {
    const shippingDate = getShippingDate(item)
    return shippingDate && (!startDate || shippingDate >= startDate) &&
      (!endDate || shippingDate <= endDate)
  })
}

export const filterArrivalItemsByReceivedDate = (items, startDate, endDate) => {
  if (!startDate && !endDate) return items
  if (startDate && endDate && startDate > endDate) return []
  return items.filter((item) => {
    const info = item?.arrival_info || {}
    // Only records with a usable 受信日 can match an active date filter.
    if (String(info.arrival_data_category || '').trim() !== 'V' ||
        formatArrivalDate(info.arrival_received_at, true) === '-') return false
    const date = getCalendarDate(info.arrival_received_at)
    return date && (!startDate || date >= startDate) && (!endDate || date <= endDate)
  })
}

export const updateArrivalSelections = (selected, visibleInventory, select, preserveOutside) => {
  const next = { ...selected }
  Object.entries(visibleInventory).forEach(([code, items]) => {
    const visible = new Set(items.map((item) => item.niukabango))
    const previous = preserveOutside ? (selected[code] || []) : []
    next[code] = select
      ? Array.from(new Set([...previous, ...visible]))
      : previous.filter((number) => !visible.has(number))
  })
  return next
}

export const attachArrivalTimestamps = (inventoryData, rawInventory = []) => {
  const timestampsByNyuka = new Map()
  rawInventory.forEach((source) => {
    const nyukaNumber = String(
      source?.nyuka_number ??
        source?.niukabango ??
        source?.ID ??
        source?.id ??
        ''
    )
    if (!nyukaNumber || timestampsByNyuka.has(nyukaNumber)) return
    timestampsByNyuka.set(nyukaNumber, {
      arrival_shipping_at: source.arrival_shipping_at ?? null,
      arrival_received_at: source.arrival_received_at ?? null
    })
  })

  return Object.fromEntries(
    Object.entries(inventoryData || {}).map(([productCode, nyukaItems]) => [
      productCode,
      (nyukaItems || []).map((nyuka) => {
        const timestamps = timestampsByNyuka.get(
          String(nyuka?.niukabango ?? '')
        )
        if (!timestamps) return nyuka
        return {
          ...nyuka,
          items: (nyuka.items || []).map((item) => ({
            ...item,
            arrival_shipping_at:
              item.arrival_shipping_at ?? timestamps.arrival_shipping_at,
            arrival_received_at:
              item.arrival_received_at ?? timestamps.arrival_received_at
          }))
        }
      })
    ])
  )
}

export const getArrivalDisplay = (item) => {
  const source = item?.arrival_info || {}
  const dataCategory = String(source.arrival_data_category || '').trim()
  const saleType =
    source.arrival_sale_dv === null || source.arrival_sale_dv === undefined
      ? '-'
      : Number(source.arrival_sale_dv) === 0
        ? '【委託】'
        : '【買付】'
  const reserveInfo =
    dataCategory === 'T'
      ? [
          source.arrival_transport_no,
          source.arrival_shipment_no,
          source.arrival_reserve_note
        ]
          .filter((value) => value !== null && value !== undefined && String(value).trim())
          .map((value) => String(value).trim())
          .join(' ')
      : ''

  return {
    shipper: arrivalText(source.arrival_shipper_name || item?.items?.[0]?.shipper_code),
    quantity: arrivalText(source.arrival_quantity),
    saleType,
    dataCategory: arrivalText(source.arrival_data_category),
    productName: arrivalText(
      dataCategory === 'V' ? source.arrival_product_kana : source.arrival_meisei_name
    ),
    shippingDate: formatArrivalDate(source.arrival_shipping_at),
    receivedDate:
      dataCategory === 'V'
        ? formatArrivalDate(source.arrival_received_at, true)
        : '-',
    waybill: arrivalText(
      dataCategory === 'T'
        ? source.arrival_transport_company_cd
        : source.arrival_shipment_no
    ),
    truckNumber: arrivalText(
      dataCategory === 'T'
        ? source.arrival_transport_company_kana
        : source.arrival_transport_no
    ),
    arrivalNumber: arrivalText(source.arrival_number),
    cargoNote: dataCategory === 'T' ? arrivalText(source.arrival_detail_note) : '-',
    reserveInfo: arrivalText(reserveInfo)
  }
}

export const compareArrivalItems = (left, right) => {
  const a = left.arrival_info || {}
  const b = right.arrival_info || {}
  const compare = (first, second) =>
    String(first || '').localeCompare(String(second || ''), 'ja')
  return (
    compare(b.arrival_sale_date, a.arrival_sale_date) ||
    compare(a.arrival_shipper_cd, b.arrival_shipper_cd) ||
    compare(b.arrival_updated_at, a.arrival_updated_at)
  )
}

export const compareArrivalShippingDates = (left, right, direction = 'desc') => {
  const leftDate = getShippingDate(left)
  const rightDate = getShippingDate(right)

  if (!leftDate && rightDate) return 1
  if (leftDate && !rightDate) return -1
  if (leftDate && rightDate && leftDate !== rightDate) {
    const dateOrder = leftDate.localeCompare(rightDate)
    return direction === 'asc' ? dateOrder : -dateOrder
  }
  return compareArrivalItems(left, right)
}
